import { useAccountDeletionStatus } from "@orbit/api-client-react";
import { Link } from "wouter";

/**
 * Cross-cutting banner shown on /profile and /cards while the
 * customer is in the 7-day grace window after requesting deletion.
 * Links straight to the cancel surface so a single tap reverses the
 * decision. Renders nothing when not in grace (or when the query is
 * still loading) to avoid layout flash.
 *
 * The status query is shared with ProfileAccountDelete via the
 * `["account-deletion-status"]` query key, so cancelling there
 * invalidates this banner without a manual refresh.
 */
export function DeletionScheduledBanner(props: { enabled?: boolean }) {
  const enabled = props.enabled ?? true;
  const q = useAccountDeletionStatus({ enabled });

  if (!enabled) return null;
  if (
    !q.data ||
    !q.data.deletionRequestedAt ||
    !q.data.scheduledCompletionAt
  ) {
    return null;
  }

  const scheduled = new Date(q.data.scheduledCompletionAt);
  const dateLabel = scheduled.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
      <p className="font-semibold">Your account is scheduled for deletion.</p>
      <p className="mt-1 text-xs">
        We'll permanently delete your profile on {dateLabel}. Cancel any time
        before then.
      </p>
      <Link
        href="/profile/account-delete"
        className="mt-2 inline-block text-xs font-medium underline"
      >
        Cancel deletion →
      </Link>
    </div>
  );
}
